<script setup lang="ts">
import { datadogLogs } from "@datadog/browser-logs";
import { useQueryClient } from "@tanstack/vue-query";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import { computed, reactive, toRefs, watch } from "vue";
import { useToast } from "vue-toast-notification";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "@/constants";
import { getQueryKey as getActualUnitsKey, useGetActualUnits } from "@/providers/housing/useGetActualUnits.ts";
import {
  getQueryKey as patchActualUnitsKey,
  usePatchActualUnitNames,
} from "@/providers/housing/usePatchActualUnitNames.ts";
import { INVALID_NAME_MSG } from "./constants.ts";
import { actualUnitInstancesPresets } from "./styles";
import { type ActualUnit, ActualUnitsListSchema, type Emits, type LocalState, type Props } from "./types.ts";

defineOptions({
  name: "ActualUnitInstances",
});

const toast = useToast();
const queryClient = useQueryClient();

const props = withDefaults(defineProps<Props>(), {
  unitId: "",
  unitTitle: "",
  modalIsOpen: false,
  housingId: "",
});
const { modalIsOpen, housingId, unitId, unitTitle } = toRefs(props);

const localState = reactive<LocalState>({
  editableActualUnits: [],
});

const emit = defineEmits<Emits>();

const handleCloseModal = () => {
  emit("closeModal");
};

const enableActualUnitsQuery = computed(() => Boolean(housingId.value) && Boolean(unitId.value));

const actualUnitsToPatch = computed(() =>
  localState.editableActualUnits
    .filter((unit) => unit.update)
    .map((unit) => {
      return {
        id: unit.id,
        name: unit.name,
      };
    }),
);

const { data: actualUnits, isLoading } = useGetActualUnits({
  housingId,
  unitId,
  field: "name",
  order: "ASC",
  skip: 0,
  limit: 1000,
  useQueryOptions: { enabled: enableActualUnitsQuery },
});

const { mutateAsync: patchActualUnitNames, isPending: currentlyPatching } = usePatchActualUnitNames({
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: getActualUnitsKey(housingId, unitId, 1000, 0, "name", "ASC") });
    await queryClient.invalidateQueries({ queryKey: patchActualUnitsKey() });
    toast.open({
      message: `Updated names of unit instances.`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
    handleCloseModal();
  },
  onError: (error) => {
    datadogLogs.logger.warn("Failed to patch actual unit names.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't patch actual unit names, try again.`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});

watch(
  () => actualUnits.value,
  (newActualUnits) => {
    if (newActualUnits) {
      localState.editableActualUnits = [...newActualUnits.items]
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((unit) => {
          const item: ActualUnit = {
            ordinal: unit.ordinal,
            id: unit.id,
            status: unit.status.name,
            name: unit.name ?? "",
            update: false, // flag to filter items to bulk update
            errors: [],
          };
          return item;
        });
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

const saveActualUnits = async () => {
  const validationResult = ActualUnitsListSchema.safeParse(localState.editableActualUnits);
  if (validationResult.success) {
    await patchActualUnitNames({
      payload: {
        items: actualUnitsToPatch.value,
      },
    });
  } else {
    toast.open({
      message: INVALID_NAME_MSG,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });

    validationResult.error.issues.forEach((error) => {
      const indexWithError = error.path[0] as number;
      if (indexWithError in localState.editableActualUnits) {
        localState.editableActualUnits[indexWithError].errors?.push(error.message);
      }
    });
  }
};
</script>

<template>
      <Dialog :visible="modalIsOpen" :modal="true" @update:visible="handleCloseModal" :pt="actualUnitInstancesPresets.modal" dismissableMask>
        <template #header>
          <div class="font-semibold font-montserrat text-slate-700" data-testid="actualunittitle">
            Manage Unit Instances for {{ unitTitle }}
          </div>
        </template>
        <template #closeicon>
          <CloseIcon size="18" stroke="#037f80" stroke-width="1.75" />
        </template>
        <div
          class="mx-auto bg-white z-50 relative"
        >
          <Spinner
            v-if="isLoading"
            class="relative h-65vh bg-blue-100"
          />
          <!-- Content -->
          <div v-else>
            <div class="max-h-96 overflow-auto">
              <div class="grid grid-cols-3">
                <!-- Header -->
                 <div class="text-sm text-left font-semibold py-3 px-2 bg-gray-125 border-b-[1px] border-solid border-b-gray-200">
                  <p>Unit Instance</p>
                 </div>
                 <div class="text-sm text-left font-semibold py-3 px-2 bg-gray-125 border-b-[1px] border-solid border-b-gray-200">
                  <p>Name</p>
                 </div>
                 <div class="text-sm text-left font-semibold py-3 px-2 bg-gray-125 border-b-[1px] border-solid border-b-gray-200">
                  <p>Status</p>
                 </div>
                <!-- Units -->

                <template v-for="actualUnit in localState.editableActualUnits" :key="actualUnit.id">
                  <div class="font-montserrat py-3 px-2">
                    {{ actualUnit.ordinal }}
                  </div>
                  <div class="font-montserrat py-3 px-2">
                    <InputText type="text" class="w-full" v-model="actualUnit.name" @update:model-value="actualUnit.update = true" :invalid="actualUnit.errors.length > 0" />
                    <div class="block" v-if="actualUnit.errors.length">
                      <p class="text-error-900" v-for="error in actualUnit.errors">
                        {{ error }}
                      </p>
                    </div>
                  </div>
                  <div class="font-montserrat py-3 px-2">
                    {{ actualUnit.status }}
                  </div>
                </template>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end mt-6 mb-6 space-x-4">
              <BaseButton
                class="text-base font-medium"
                outlined
                @click="handleCloseModal"
              >
                Cancel
              </BaseButton>
              <ButtonWithSpinner
                id="saveHousingFulfillmentOrderButton"
                :prop-loading="currentlyPatching"
                class="text-base font-semibold !h-7 w-24"
                data-cy="save-order-button"
                variant="secondary"
                variant-type="normal"
                @click.prevent="saveActualUnits"
              >
                <span>Save</span>
              </ButtonWithSpinner>
            </div>
          </div>
        </div>
      </Dialog>
</template>
